/**
 * PlayerSoundToggle
 *
 * Mute/unmute button in the header — and, while the Narrator is speaking, a
 * transport.
 *
 * It grows rather than sitting next to a second control, because a stop button
 * that is always visible is a stop button that is dead most of the time. There
 * is nothing to control until something is talking, so the affordance appears
 * exactly when it becomes true and collapses when it stops being true.
 *
 * The same rule gives music its credit: while a track is playing, a small
 * button opens what is playing and the full credits list. The mood music is
 * CC-BY, and players hear it too, so they need to be able to see the credit.
 */

import { useEffect, useRef, useState } from 'react';
import { useAudioStore } from '../stores/audioStore';
import { Icon } from './Icon';
import { CreditLine, MusicCredits } from './MusicCredits';

const GOLD = '#d4af37';

export function PlayerSoundToggle() {
  const isMuted = useAudioStore((s) => s.isMuted);
  const setMuted = useAudioStore((s) => s.setMuted);
  const currentMood = useAudioStore((s) => s.currentMood);
  const activeLayers = useAudioStore((s) => s.activeLayers);
  const isSpeaking = useAudioStore((s) => s.isSpeaking);
  const voiceEnabled = useAudioStore((s) => s.voiceEnabled);
  const lastSpokenText = useAudioStore((s) => s.lastSpokenText);
  const currentTrack = useAudioStore((s) => s.currentTrack);
  const [showCredits, setShowCredits] = useState(false);
  const creditsRef = useRef<HTMLDivElement>(null);

  // Close the credits popover on an outside click or Escape.
  useEffect(() => {
    if (!showCredits) return;
    const onDown = (e: MouseEvent) => {
      if (!creditsRef.current?.contains(e.target as Node)) setShowCredits(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowCredits(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [showCredits]);

  const hasAudio = currentMood || activeLayers.length > 0;
  const active = !isMuted && hasAudio;
  const iconColor = isMuted ? 'rgba(192,192,192,0.35)' : active ? GOLD : 'rgba(192,192,192,0.5)';

  const stop = () => {
    void import('../audio/voice').then(({ stopSpeaking }) => stopSpeaking());
  };
  const replay = () => {
    void import('../audio/voice').then(({ replayLast }) => replayLast());
  };

  // --- Speaking: the button becomes a transport --------------------------
  if (isSpeaking) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 28,
          padding: '0 10px 0 12px',
          background: 'rgba(212,175,55,0.08)',
          border: `1px solid ${GOLD}55`,
          borderRadius: 14,
          boxShadow: `0 0 10px ${GOLD}25`,
        }}
        title="The Narrator is speaking"
      >
        {/* Three bars that rise and fall — reads as speech without needing a
            label, and does not imply a progress position we do not know. */}
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 12 }}>
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              style={{
                width: 2,
                background: GOLD,
                borderRadius: 1,
                animation: `narrator-bar 900ms ease-in-out ${i * 150}ms infinite`,
              }}
            />
          ))}
        </div>

        <button
          onClick={stop}
          title="Stop the Narrator"
          style={{
            width: 18,
            height: 18,
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: GOLD,
          }}
        >
          {/* A square, because this genuinely stops rather than pausing —
              there is no resume from the middle of a synthesised sentence. */}
          <span style={{ width: 9, height: 9, background: GOLD, borderRadius: 1, display: 'block' }} />
        </button>
      </div>
    );
  }

  // --- Idle, but there is something to replay ----------------------------
  const canReplay = voiceEnabled && !!lastSpokenText;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <button
        onClick={() => setMuted(!isMuted)}
        title={isMuted ? 'Unmute audio' : 'Mute audio'}
        style={{
          width: 28,
          height: 28,
          padding: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'transparent',
          border: `1px solid ${active ? `${GOLD}40` : 'rgba(192,192,192,0.2)'}`,
          borderRadius: '50%',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          boxShadow: active ? `0 0 8px ${GOLD}30` : 'none',
          opacity: isMuted ? 0.5 : 1,
        }}
      >
        <Icon name={isMuted ? 'pause' : 'play'} size={14} color={iconColor} />
      </button>

      {canReplay && (
        <button
          onClick={replay}
          title="Read the last narration again"
          style={{
            width: 22,
            height: 22,
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            opacity: 0.55,
            transition: 'opacity 0.2s ease',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
          onMouseLeave={(e) => { e.currentTarget.style.opacity = '0.55'; }}
        >
          <Icon name="refresh-cw" size={12} color={GOLD} />
        </button>
      )}

      {currentTrack && (
        <div ref={creditsRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setShowCredits(!showCredits)}
            title={`Now playing: ${currentTrack.title}${currentTrack.artist ? ` by ${currentTrack.artist}` : ''}. Music credits`}
            style={{
              width: 22,
              height: 22,
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              opacity: showCredits ? 1 : 0.55,
              transition: 'opacity 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; }}
            onMouseLeave={(e) => { if (!showCredits) e.currentTarget.style.opacity = '0.55'; }}
          >
            <Icon name="scroll" size={12} color={GOLD} />
          </button>

          {showCredits && (
            <div
              style={{
                position: 'absolute',
                top: 30,
                right: 0,
                width: 320,
                maxWidth: 'calc(100vw - 32px)',
                padding: 12,
                background: 'rgba(10, 8, 12, 0.97)',
                border: `1px solid ${GOLD}55`,
                borderRadius: 4,
                boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                gap: 10,
              }}
            >
              <div>
                <div style={{ fontSize: 9, fontWeight: 600, color: GOLD, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 4 }}>
                  Now playing
                </div>
                <CreditLine track={currentTrack} highlighted />
              </div>
              <div>
                <div style={{ fontSize: 9, fontWeight: 600, color: 'rgba(192,192,192,0.7)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6 }}>
                  Music credits
                </div>
                <MusicCredits maxHeight={280} highlightId={currentTrack.id} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
